import {
  CalendarModeOptions,
  CoreCalendarViewModel,
  DayObject,
  ICoreCalendarUseCase,
  MonthObject,
  UseCoreCalendarArgs,
} from 'frontcore/presentation/calendar/domain/types'
import { computed, reactive, ref } from 'vue'
import {
  DAYS_IN_A_WEEK,
  getEndOfWeek,
  getStartOfWeek,
  getWeekdayNames,
  getWeekEnd,
  getWeekNumber,
  getWeekStart,
} from 'frontcore/domain/dates/date-utils'
import { getMonthShortName } from 'frontcore/i18n/index'

const getDefaultDateValue = (dateInfo: CoreCalendarViewModel['selectedDate'], range: boolean) : Date | null => {
  if (range && Array.isArray(dateInfo) && dateInfo.length > 0) {
    const dateStartValue = new Date(dateInfo[0].getTime())
    dateStartValue.setHours(0, 0, 0, 0)
    return dateStartValue
  } else if (dateInfo instanceof Date) {
    return dateInfo ?? null
  }
  return null
}

const getDefaultEndDateValue = (dateInfo: CoreCalendarViewModel['selectedDate'], range: boolean) : Date | null => {
  if (range && Array.isArray(dateInfo) && dateInfo.length > 1) {
    const dateEndValue = new Date(dateInfo[1].getTime())
    dateEndValue.setHours(0, 0, 0, 0)
    return dateEndValue
  }
  return null
}

const isSameDay = (date1: Date, date2: Date) => {
  date1.setHours(0, 0, 0, 0)
  date2.setHours(0, 0, 0, 0)
  return date1.getTime() === date2.getTime()
}

const isDateInArray = (date: Date, disabledDates: Date[]): boolean => {
  return disabledDates.some(disabledDate => isSameDay(disabledDate, date))
}

const useCoreCalendar = ({
  disabledDays = ref([]),
  disableFutureDays = ref(false),
  disablePastDays = ref(false),
  nullable = ref(true),
  range = ref(false),
  selectionMode = ref(CalendarModeOptions.SELECT_DAY),
  value = ref(null),
} : UseCoreCalendarArgs = {}): ICoreCalendarUseCase => {

  const dateValue = computed(() => getDefaultDateValue(value.value, range.value) ?? new Date())

  const selectedDate = ref(new Date(dateValue.value.getTime()))
  selectedDate.value.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const handleDisabledDates = (date: Date) => {
    if (disabledDays.value) {
      if (Array.isArray(disabledDays.value)) {
        return isDateInArray(date, disabledDays.value)
      } else if (typeof disabledDays.value === 'function') {
        return disabledDays.value(date)
      }
    }
    return false
  }


  const isDisabled = (date: Date) => {
    const isPastDate = date < today
    const isFutureDate = date > today

    return (isPastDate && disablePastDays.value) || (isFutureDate && disableFutureDays.value) || handleDisabledDates(date)
  }

  const isWeekDisabled = (date: Date) => {
    const weekStartDate = getStartOfWeek(date)
    const weekEndDate = getEndOfWeek(date)
    return isDisabled(weekStartDate) || isDisabled(weekEndDate)
  }

  const isDisabledMonth = (date: Date) => {
    const todaysMonth = new Date()
    todaysMonth.setDate(1)
    todaysMonth.setHours(0, 0, 0, 0)
    const isPastDate = date < todaysMonth
    const isFutureDate = date > todaysMonth

    return (isPastDate && disablePastDays.value) || (isFutureDate && disableFutureDays.value)
  }

  const updateInternalValue = (dateInfo: CoreCalendarViewModel['selectedDate']) => {
    viewmodel.selectedDate = dateInfo
    if (range.value && Array.isArray(dateInfo)) {
      switch (dateInfo.length) {
      case 1:
        viewmodel.startDate = dateInfo[0]
        viewmodel.endDate = null
        break
      case 2:
        viewmodel.startDate = dateInfo[0]
        viewmodel.endDate = dateInfo[1]
        break
      default:
        viewmodel.startDate = null
        viewmodel.endDate = null
      }
    }
  }

  const setCalendarSelectedDate = () => {
    const initialDate = getDefaultDateValue(viewmodel.selectedDate, range.value)
    viewmodel.currentPickerMonth = (initialDate?.getMonth() ?? new Date().getMonth()) + 1
    viewmodel.currentPickerYear = initialDate?.getFullYear() ?? new Date().getFullYear()
  }

  const offsetDays = computed(() => {
    const firstDayOfTheMonth = new Date(
      viewmodel.currentPickerYear,
      viewmodel.currentPickerMonth - 1,
      1
    )
    const diff = firstDayOfTheMonth.getDay() - getWeekStart()
    if (diff < 0) {
      return firstDayOfTheMonth.getDay() + DAYS_IN_A_WEEK - getWeekStart()
    }
    return diff
  })

  const getDayOfNextOrLastMonthObject = (customDate: Date, selectedStartDateTimeValue: number | undefined, selectedEnDateTimeValue: number | undefined) => {
    return {
      date: selectionMode.value === CalendarModeOptions.SELECT_WEEK ? customDate : null,
      isDisabled: selectionMode.value === CalendarModeOptions.SELECT_WEEK ? isDisabled(customDate) : true,
      isSelected: dateIsSelectedInViewModel(customDate),
      isCurrentDate: false,
      isInRange: dateIsInRange(customDate),
      isStartDate: customDate.getTime() === selectedStartDateTimeValue,
      isEndDate: customDate.getTime() === selectedEnDateTimeValue,
      weekNumber: getWeekNumber(customDate),
      isFirstDayOfWeek: customDate.getDay() === getWeekStart(),
      isLastDayOfWeek: customDate.getDay() === getWeekEnd(),
      isWeekDisabled: isWeekDisabled(customDate),
    }
  }

  const daysOfNextMonth = computed(() => {
    const days: DayObject[] = []
    const date = new Date(viewmodel.currentPickerYear, viewmodel.currentPickerMonth, 1)
    const selectedEndDate = viewmodel.endDate
    if (selectedEndDate) {
      selectedEndDate.setHours(0,0,0,0)
    }
    const selectedStartDateTimeValue = viewmodel.startDate?.getTime()
    const selectedEnDateTimeValue = selectedEndDate?.getTime()
    const numDaysOfNextMonth = 7 - (((daysInMonth.value.length + daysOfLastMonth.value.length) % 7) || 7)

    for(let i = 0; i < numDaysOfNextMonth; i++) {
      const customDate = new Date(date.setDate(i + 1))
      days.push(getDayOfNextOrLastMonthObject(customDate, selectedStartDateTimeValue, selectedEnDateTimeValue))
    }
    return days
  })

  const daysOfLastMonth = computed(() => {
    const days: DayObject[] = []
    const date = new Date(viewmodel.currentPickerYear, viewmodel.currentPickerMonth - 1, 1)
    const selectedEndDate = viewmodel.endDate
    if (selectedEndDate) {
      selectedEndDate.setHours(0,0,0,0)
    }
    const selectedStartDateTimeValue = viewmodel.startDate?.getTime()
    const selectedEnDateTimeValue = selectedEndDate?.getTime()

    for(let i = 0; i < offsetDays.value; i++) {
      const customDate = new Date(new Date(date).setDate(date.getDate() - (offsetDays.value - i)))
      days.push(getDayOfNextOrLastMonthObject(customDate, selectedStartDateTimeValue, selectedEnDateTimeValue))
    }
    return days
  })

  const dateIsSelectedInViewModel = (date: Date): boolean => {
    const currentDateTime = date.getTime()
    if (range.value) {
      return currentDateTime === viewmodel.startDate?.getTime() || currentDateTime === viewmodel.endDate?.getTime()
    }
    return currentDateTime === getDefaultDateValue(viewmodel.selectedDate, range.value)?.getTime()
  }

  const dateIsInRange = (date: Date): boolean => {
    const currentDateTime = date.getTime()
    if (range.value && viewmodel.startDate && viewmodel.endDate) {
      return currentDateTime >= viewmodel.startDate.getTime() && currentDateTime <= viewmodel.endDate.getTime()
    }
    return false
  }

  const daysInMonth = computed(() => {
    const date = new Date(viewmodel.currentPickerYear, viewmodel.currentPickerMonth - 1, 1)
    const days: DayObject[] = []
    const todayTime = today.getTime()
    const selectedEndDate = viewmodel.endDate
    selectedEndDate?.setHours(0,0,0,0)
    const selectedEnDateTimeValue = selectedEndDate?.getTime()
    const selectedStartDateTimeValue = viewmodel.startDate?.getTime()
    while (date.getMonth() === viewmodel.currentPickerMonth - 1) {
      const customDate = new Date(date)
      days.push({
        date: customDate,
        isDisabled: isDisabled(date),
        isSelected: dateIsSelectedInViewModel(date),
        isCurrentDate: customDate.getTime() === todayTime,
        isInRange: dateIsInRange(date),
        isStartDate: customDate.getTime() === selectedStartDateTimeValue,
        isEndDate: customDate.getTime() === selectedEnDateTimeValue,
        weekNumber: getWeekNumber(customDate),
        isFirstDayOfWeek: customDate.getDay() === getWeekStart(),
        isLastDayOfWeek: customDate.getDay() === getWeekEnd(),
        isWeekDisabled: isWeekDisabled(date),
      })
      date.setDate(date.getDate() + 1)
    }
    return days
  })

  const monthsInYear = computed(() => {
    const date = new Date(viewmodel.currentPickerYear, 0, 1)
    const months: MonthObject[] = []
    while (date.getFullYear() === viewmodel.currentPickerYear) {
      months.push({
        isDisabled: isDisabledMonth(date),
        index: date.getMonth(),
        name: getMonthShortName(date.getMonth()),
      })
      date.setMonth(date.getMonth() + 1)
    }
    return months
  })

  const firstWeekNumber = computed(() => {
    const date = new Date(viewmodel.currentPickerYear, viewmodel.currentPickerMonth - 1, 1)
    return getWeekNumber(date)
  })

  const updateMonth = (month: number) => {
    viewmodel.currentPickerMonth = month
  }
  const updateMode = (mode: CalendarModeOptions) => {
    viewmodel.mode = mode
  }
  const updateYear = (year: number) => {
    viewmodel.currentPickerYear = year
  }

  const viewmodel: CoreCalendarViewModel = reactive({
    currentPickerMonth: selectedDate.value.getMonth(),
    currentPickerYear: selectedDate.value.getFullYear(),
    daysInMonth: daysInMonth,
    daysOfLastMonth: daysOfLastMonth,
    daysOfNextMonth: daysOfNextMonth,
    endDate: getDefaultEndDateValue(value.value, range.value),
    firstWeekNumber: firstWeekNumber,
    hasError: false,
    isTodayDisabled: isDisabled(today),
    mode: selectionMode.value,
    monthsInYear: monthsInYear,
    offsetDays: offsetDays,
    selectedDate: nullable.value ? getDefaultDateValue(value.value, range.value) : selectedDate.value,
    startDate: getDefaultDateValue(value.value, range.value),
    weekdayNames: getWeekdayNames(),
  })

  return {
    viewmodel,
    setCalendarSelectedDate,
    updateInternalValue,
    updateMonth,
    updateMode,
    updateYear,
  }
}

export default useCoreCalendar
